import React, { useEffect, useMemo, useRef, useState } from "react";

import "./BlackboxPaywall.scss";
import { Modal } from "@material-ui/core";
import { Button } from "@mui/material";
import { connect } from "react-redux";
import moment from "moment";

import { useAuth } from "../../contexts/Auth";
import blackboxLogo from "../../assets/images/blackbox/blackbox-logo-color.svg";
import BeautifulSkeleton from "../common/Skeletons/BeautifulSkeleton";
import ContactForm from "../EmeraldSignup/ContactForm";
import {
  BB_PAYWALL_BODY,
  BB_PAYWALL_DISCLAIMER,
  BB_PAYWALL_EYEBROW,
  BB_PAYWALL_FEATURES,
  BB_PAYWALL_HEADLINE,
  BB_PAYWALL_INCLUDED,
  BB_PAYWALL_INCLUDED_CHIP,
  BB_PAYWALL_TAGLINE,
  BLACKBOX_PRODUCT_TYPE,
} from "./constants";

/* Scroll-reveal with the motion from the prototype (fade up 20px, 0.6s, ease
 * [0.22, 1, 0.36, 1], -40px in-view margin), done with IntersectionObserver
 * and a CSS transition like the Bitcoin Navigator paywall, because the
 * dashboard has no animation library. prefers-reduced-motion renders
 * everything in place via CSS. */
const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "-40px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`bb-reveal ${visible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

/* Icons: the feature icons take the primary blue from CSS (currentColor). */

const FEATURE_ICONS = {
  signals: (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      className="bb-paywall__feature-icon"
      aria-hidden="true"
    >
      <rect x="2" y="16" width="4" height="6" rx="1" fill="currentColor" />
      <rect x="8" y="12" width="4" height="10" rx="1" fill="currentColor" />
      <rect x="14" y="7" width="4" height="15" rx="1" fill="currentColor" />
      <rect x="20" y="2" width="4" height="20" rx="1" fill="currentColor" />
    </svg>
  ),
  wealthSeries: (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      className="bb-paywall__feature-icon"
      aria-hidden="true"
    >
      <path
        d="M16.358 6C15.3004 8.37101 17.1768 9.5821 19.5648 10.9297C22.0723 12.3454 23.9998 13.096 23.9998 15.2111C23.9998 16.9169 22.6864 17.9403 21.0659 17.9403C22.4987 15.7058 21.4753 14.7506 17.6885 12.6354C15.1981 11.2537 14.0723 10.1621 14.0723 8.33689C14.0723 7.0064 14.9422 6 16.358 6ZM20.5883 6H23.1469V8.55864C22.6352 7.02346 22.1235 6.51173 20.5883 6Z"
        fill="currentColor"
      />
      <path
        d="M14.8092 17.8919C15.4205 17.8799 16.8104 17.8918 17.8173 17.8919C16.3193 17.4305 16.3193 16.9811 15.5703 15.3333C15.5703 15.3333 15.7201 17.8868 14.8092 17.8919Z"
        fill="currentColor"
      />
      <path
        d="M5.54373 8.55864L7.43712 12.7548C8.59704 15.3646 7.7271 16.8657 7.14714 17.9403L2.8998 8.55864C2.18338 7.02346 1.31344 6.34115 0 6C3.5821 6 4.72496 6.69936 5.54373 8.55864ZM11.5651 8.55864L13.4585 12.7548C14.6184 15.3646 13.7484 16.8657 13.1685 17.9403L8.92114 8.55864C8.20472 7.02346 7.33478 6.34115 6.02134 6H12.5544C11.4968 6.34115 10.8828 7.02346 11.5651 8.55864Z"
        fill="currentColor"
      />
    </svg>
  ),
};

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#9db4ff"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="bb-paywall__check"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * Blackbox: logged-in paywall (TRAD-1293 AC5, restyled in TRAD-1317 to the
 * Wealth Series card look with a dark top bar in both themes). The dashboard
 * prototype page paired with the standard sales contact flow: "Contact Sales" opens the
 * shared ContactForm (product type BLACKBOX, POST /subscriptions/request-access)
 * and requests are held to one per week via userSettings, like the other
 * paywalls. There is no Blackbox client dashboard yet, so Black Box clients
 * see this page too (the sidebar marks them Subscribed).
 */
const BlackboxPaywall = ({ userSettings }) => {
  const [open, setOpen] = useState(false);
  const { loading } = useAuth();
  const handleClose = () => setOpen(false);

  // Within 1 week of the last request: disabled "Access Requested"
  const hasRequested = useMemo(() => {
    const submitDate = userSettings?.blackboxContact;
    return submitDate
      ? moment().isBefore(moment(submitDate).add(1, "week"))
      : false;
  }, [userSettings]);

  return (
    <div className="bb-paywall">
      <div className="bb-paywall__bg" aria-hidden="true" />
      <div className="bb-paywall__scrim" aria-hidden="true" />

      <div className="bb-paywall__content">
        <div className="bb-paywall__hero">
          <div>
            <Reveal delay={0.04}>
              <p className="bb-paywall__eyebrow">{BB_PAYWALL_EYEBROW}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <h1 className="bb-paywall__headline">{BB_PAYWALL_HEADLINE}</h1>
            </Reveal>

            <Reveal delay={0.16}>
              <p className="bb-paywall__tagline">{BB_PAYWALL_TAGLINE}</p>
            </Reveal>

            <Reveal delay={0.22}>
              <p className="bb-paywall__body">{BB_PAYWALL_BODY}</p>
            </Reveal>

            <Reveal delay={0.28}>
              <div className="bb-paywall__cta-row">
                {loading ? (
                  <BeautifulSkeleton width={163} height={48} />
                ) : (
                  <Button
                    variant="contained"
                    className="bb-paywall__cta"
                    onClick={() => setOpen(true)}
                    disabled={hasRequested}
                  >
                    {hasRequested ? "Access Requested" : "Contact Sales"}
                  </Button>
                )}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.3} className="bb-paywall__included-col">
            <div className="bb-paywall__included">
              <span className="bb-paywall__included-glow" aria-hidden="true" />
              <div className="bb-paywall__included-top">
                <img
                  className="bb-paywall__included-logo"
                  src={blackboxLogo}
                  alt="Blackbox"
                />
                <span className="bb-paywall__chip">
                  {BB_PAYWALL_INCLUDED_CHIP}
                </span>
              </div>
              <ul className="bb-paywall__included-list">
                {BB_PAYWALL_INCLUDED.map((item) => (
                  <li key={item} className="bb-paywall__included-item">
                    <span>{item}</span>
                    <CheckIcon />
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <div className="bb-paywall__features">
          {BB_PAYWALL_FEATURES.map((feature, index) => (
            <Reveal key={feature.title} delay={0.34 + index * 0.06}>
              {FEATURE_ICONS[feature.icon]}
              <h3 className="bb-paywall__feature-title">{feature.title}</h3>
              <p className="bb-paywall__feature-body">{feature.body}</p>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="bb-paywall__footer">
        <p className="bb-paywall__disclaimer">{BB_PAYWALL_DISCLAIMER}</p>
      </div>

      <Modal open={open} onClose={handleClose}>
        {/* MUI v4 TrapFocus needs a child that can hold a ref. The connected
            ContactForm cannot, so without this focus stays behind the popup
            and Escape does nothing. Static, so the card keeps centering on
            the modal root. */}
        <div tabIndex={-1} className="bb-paywall__modal-focus">
          <ContactForm
            product_type={BLACKBOX_PRODUCT_TYPE}
            onClose={handleClose}
          />
        </div>
      </Modal>
    </div>
  );
};

const stateToProps = (state) => ({
  userSettings: state.userSettings.userSettings,
});

export default connect(stateToProps)(BlackboxPaywall);
